import React, { useEffect, useRef } from "react";
import { PageHeader } from "../../component/Header";
import { H3, H4 } from "../../component/Typography";
import styled from "styled-components";
import { useMediaQuery } from "react-responsive";
import { Col, Form, Modal, Row, Select, Space } from "antd";
import { DateInput, RadioInput, TextInput } from "../../component/Input";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import { PrimaryButton, SecondaryButton } from "../../component/Button";
import { useNavigate } from "react-router-dom";
import {
  saveDraft,
  loadDraft,
  clearDraft,
  formatRelativeTime,
} from "../../utils/draftStorage";
import { hasKlaLicense } from "../../utils/license";

const { Item } = Form;

// JSON.stringify가 dayjs를 ISO 문자열로 만들어버리므로
// 복원 시 이 필드들은 다시 dayjs 객체로 변환해야 함.
const DATE_FIELDS = ["birthday", "examdate"];

const SAVE_DEBOUNCE_MS = 1000;

const FIELD_REQUIRED_TEXT = (
  <span style={{ color: "var(--fg-primary)" }}>(필수)</span>
);

function Input(props) {
  const { user } = props;
  const isDesktop = useMediaQuery({ query: "(min-width: 576px)" });
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // draft 키는 로그인된 사용자별로 분리 → 공용 PC에서 다른 사람이 봐도 자기 것만 보임
  const draftKey = user?.id ? `kla_input_draft_${user.id}` : null;
  const debounceTimer = useRef(null);

  // 페이지 진입 시 한 번: 저장된 draft가 있으면 복원 여부 묻기
  useEffect(() => {
    if (!draftKey) return;
    const draft = loadDraft(draftKey);
    if (!draft) return;

    Modal.confirm({
      title: "이전 작성 내용 복원",
      content: `${formatRelativeTime(draft.saved_at)}에 작성하던 내용이 있습니다. 이어서 작성하시겠어요?`,
      okText: "복원",
      cancelText: "새로 시작",
      onOk: () => {
        // 직렬화 과정에서 ISO 문자열이 된 날짜 필드를 dayjs로 되돌림
        const restored = { ...draft.data };
        DATE_FIELDS.forEach((field) => {
          if (restored[field]) restored[field] = dayjs(restored[field]);
        });
        form.setFieldsValue(restored);
      },
      onCancel: () => {
        clearDraft(draftKey);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // 사용자가 입력할 때마다 1초 debounce로 localStorage에 자동 저장
  const handleValuesChange = (_changed, allValues) => {
    if (!draftKey) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      saveDraft(draftKey, allValues);
    }, SAVE_DEBOUNCE_MS);
  };

  // 컴포넌트 언마운트 시 타이머 정리 (메모리 leak 방지)
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const onFinish = () => {
    const formData = form.getFieldsValue();

    const fullInputText = `
    +이름 ${formData.name}
    +성별 ${formData.gender}
    +검사일 ${formData.examdate.format("YYYY-MM-DD")}
    +생년월일 ${formData.birthday.format("YYYY-MM-DD")}
    +검사자 ${formData.examiner}
    +교육기관 ${formData.school}
    +학년 ${formData.grade}
    +평가지역 ${formData.region}
    +평가기관 ${formData.institution}
    +발화종류 ${formData.utteranceType}
    ${formData.inputText}`;

    // 정상 제출되면 draft 제거 (다음에 열었을 때 옛 내용 안 보이게)
    clearDraft(draftKey);

    // console.log("fullInputText:", fullInputText, user);
    navigate("/kla/result", { state: { inputText: fullInputText, user } });
  };

  const renderField = (name, label, Component, options = {}) => (
    <Col
      span={
        isDesktop ? options.colSpanDesktop || 12 : options.colSpanMobile || 24
      }
    >
      <Item
        name={name}
        label={
          <H4>
            {label} {FIELD_REQUIRED_TEXT}
          </H4>
        }
        rules={[
          {
            required: true,
            message: `${label}${
              label === "전사 데이터" ? "를" : "을"
            } 입력해주세요.`,
          },
        ]}
        {...options.itemProps}
      >
        {Component}
      </Item>
    </Col>
  );

  // 라이센스 가드: 모든 hook 호출 이후, 실제 렌더 전.
  // KLA 라이센스 없거나 미로그인 → 메시지 모달만 표시, 본문(입력폼) 렌더 안 함.
  // 모달의 [확인] 클릭으로만 Intro로 이동 가능 (X/ESC/마스크로 닫기 차단).
  // URL 직접 입력으로 우회 접근하는 경우 차단. admin은 만료와 무관하게 통과.
  if (!hasKlaLicense(user)) {
    const isLoggedOut = !user;
    return (
      <Modal
        open
        closable={false}
        keyboard={false}
        maskClosable={false}
        title={isLoggedOut ? "로그인이 필요합니다" : "사용권 구매가 필요합니다"}
        okText="확인"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => navigate("/kla/intro", { replace: true })}
      >
        {isLoggedOut
          ? "이 기능을 사용하려면 로그인이 필요합니다."
          : "프로그램 분석 사용권 구입을 원하시면 gpspeech4all@gmail.com로 연락주세요."}
      </Modal>
    );
  }

  return (
    <div>
      <PageHeader
        title="신규 발화 분석"
        onSubmit={() => form.submit()}
        buttonText="분석하기"
      />
      <Form
        form={form}
        name="input-form"
        layout={isDesktop ? "vertical" : "horizontal"}
        requiredMark={false}
        onFinish={onFinish}
        onValuesChange={handleValuesChange}
      >
        <PageContainer>
          <InputWrapper>
            <H3>발화자 정보 입력</H3>
            <Row gutter={[16, 16]}>
              {renderField(
                "name",
                "발화자 이름",
                <TextInput
                  size="large"
                  placeholder="발화자명을 입력해주세요."
                />,
                { colSpanDesktop: 8, colSpanMobile: 24 }
              )}
              {renderField(
                "birthday",
                "생년월일",
                <DateInput
                  size="large"
                  placeholder="생년월일을 입력해주세요."
                />,
                {
                  itemProps: { initialValue: dayjs() },
                  colSpanDesktop: 8,
                  colSpanMobile: 24,
                }
              )}
              {renderField(
                "gender",
                "성별",
                <RadioInput
                  size="large"
                  options={[
                    { label: "남자", value: "남" },
                    { label: "여자", value: "여" },
                  ]}
                />,
                { colSpanDesktop: 8, colSpanMobile: 24 }
              )}
              {renderField(
                "school",
                "교육기관",
                <Select size="large" placeholder="교육 기관 선택">
                  <Select.Option value="어린이집">어린이집</Select.Option>
                  <Select.Option value="유치원">유치원</Select.Option>
                  <Select.Option value="초등학교">초등학교</Select.Option>
                  <Select.Option value="해당없음">해당없음</Select.Option>
                </Select>
              )}
              {renderField(
                "grade",
                "학년",
                <Select size="large" placeholder="학년 선택">
                  <Select.Option value="미취학">미취학</Select.Option>
                  <Select.Option value="1학년">1학년</Select.Option>
                  <Select.Option value="2학년">2학년</Select.Option>
                  <Select.Option value="3학년">3학년</Select.Option>
                  <Select.Option value="4학년">4학년</Select.Option>
                  <Select.Option value="5학년">5학년</Select.Option>
                  <Select.Option value="6학년">6학년</Select.Option>
                </Select>
              )}
            </Row>
          </InputWrapper>

          <InputWrapper>
            <H3>평가자 정보 입력</H3>
            <Row gutter={[16, 16]}>
              {renderField(
                "examiner",
                "검사자 이름",
                <TextInput
                  size="large"
                  placeholder="검사자명을 입력해주세요."
                />
              )}
              {renderField(
                "examdate",
                "검사일자",
                <DateInput
                  size="large"
                  placeholder="검사일자를 입력해주세요."
                />,
                { itemProps: { initialValue: dayjs() } }
              )}
              {renderField(
                "region",
                "평가지역",
                <TextInput
                  size="large"
                  placeholder="평가지역을 입력해주세요."
                />
              )}
              {renderField(
                "institution",
                "평가기관",
                <TextInput
                  size="large"
                  placeholder="평가기관을 입력해주세요."
                />
              )}
            </Row>
          </InputWrapper>

          <InputWrapper>
            <H3>발화 데이터 입력</H3>
            <Row gutter={[16, 16]}>
              {renderField(
                "utteranceType",
                "발화 종류",
                <RadioInput
                  size="large"
                  options={[
                    { label: "자발화", value: "자발화" },
                    { label: "이야기(회상산출)", value: "이야기(회상산출)" },
                    { label: "이야기(자발산출)", value: "이야기(자발산출)" },
                    { label: "작문", value: "작문" },
                  ]}
                />,
                { colSpanDesktop: 24 }
              )}
              {renderField(
                "inputText",
                "전사 데이터",
                <TextArea
                  size="large"
                  placeholder="전사 데이터를 입력해주세요."
                  rows={20}
                />,
                { colSpanDesktop: 24 }
              )}
            </Row>
            <Space style={{ justifyContent: "flex-end" }}>
              <SecondaryButton size="large" onClick={() => navigate(-1)}>
                취소
              </SecondaryButton>
              <PrimaryButton size="large" htmlType="submit">
                분석하기
              </PrimaryButton>
            </Space>
          </InputWrapper>
        </PageContainer>
      </Form>
    </div>
  );
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--bg-disable);
  max-width: 1280px;
  margin: 0 auto;
  gap: 16px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background-color: var(--bg-body);
`;

export default Input;
